import {
  MrcEpisode,
  MrcHistoryItem,
  SingleProfitCalculationApiResponse,
  Transaction,
} from '../../../../ProfitCalculations/types';
import { tokens } from '../tokens';
import { MrcPhase } from '../txHelpers';
import { fmtDate, fmtPrice, isCore } from './format';

const phaseColor = (
  state: MrcHistoryItem['state'],
  isTimeFrameSwitch?: boolean,
): string => {
  // A timeframe switch is no longer a pseudo-state; it is flagged separately but still shown blue.
  if (isTimeFrameSwitch) return tokens.blue;
  switch (state) {
    case 'INITIALISED':
      return tokens.green;
    case 'PENDING':
      return tokens.amber;
    case 'RESET':
      return tokens.red;
    case 'CANCELLED':
      return tokens.textMuted;
    default:
      return tokens.textMuted;
  }
};

const formatRsi = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : value.toFixed(1);

export const episodeToPhases = (episode: MrcEpisode): MrcPhase[] =>
  episode.history.map((historyItem) => ({
    name: historyItem.state,
    color: phaseColor(historyItem.state, historyItem.isTimeFrameSwitch),
    timeFrame: historyItem.timeFrame,
    startDate: fmtDate(historyItem.startTimestamp),
    endDate:
      historyItem.endTimestamp !== undefined
        ? fmtDate(historyItem.endTimestamp)
        : fmtDate(historyItem.startTimestamp),
    startPrice: fmtPrice(historyItem.startPrice),
    endPrice:
      historyItem.endPrice !== undefined ? fmtPrice(historyItem.endPrice) : '—',
    startRsi: formatRsi(
      historyItem.rsiMultiTStartValue ?? historyItem.rsiShorterTStartValue,
    ),
    endRsi: formatRsi(
      historyItem.rsiMultiTEndValue ?? historyItem.rsiShorterTEndValue,
    ),
  }));

/** Core transactions sorted oldest-first. */
const coreSortedAsc = (
  spc: SingleProfitCalculationApiResponse,
): Transaction[] =>
  [...spc.transactions]
    .filter((tx) => isCore(tx.transactionType))
    .sort(
      (a, b) =>
        (a.transactionTimestamp ?? a.timestamp) -
        (b.transactionTimestamp ?? b.timestamp),
    );

/**
 * The most recent MRC episode that resolved within (startExclusive, endInclusive].
 */
const findEpisode = (
  episodes: MrcEpisode[],
  startExclusive: number,
  endInclusive: number,
): MrcEpisode | null => {
  let latest: MrcEpisode | null = null;
  for (const episode of episodes) {
    const episodeEnd = episode.endTimestamp ?? episode.startTimestamp;
    if (episodeEnd > startExclusive && episodeEnd <= endInclusive) {
      if (
        !latest ||
        episodeEnd > (latest.endTimestamp ?? latest.startTimestamp)
      ) {
        latest = episode;
      }
    }
  }
  return latest;
};

export const buildMrcByTxId = (
  spc: SingleProfitCalculationApiResponse,
): Record<number, MrcPhase[] | null> => {
  const coreSorted = coreSortedAsc(spc);

  const result: Record<number, MrcPhase[] | null> = {};
  for (const tx of spc.transactions) result[tx.id] = null;

  for (let i = 0; i < coreSorted.length; i++) {
    const core = coreSorted[i];
    const coreTimestamp = core.transactionTimestamp ?? core.timestamp;
    const previousCoreTimestamp =
      i === 0
        ? -Infinity
        : coreSorted[i - 1].transactionTimestamp ?? coreSorted[i - 1].timestamp;
    const episode = findEpisode(
      spc.mrcStates,
      previousCoreTimestamp,
      coreTimestamp,
    );
    if (episode) {
      result[core.id] = episodeToPhases(episode);
    }
  }
  return result;
};

/** The MRC episode that resolved into a given core transaction, if any. */
export const findMrcEpisodeForTx = (
  spc: SingleProfitCalculationApiResponse,
  txId: number,
): MrcEpisode | null => {
  const coreSorted = coreSortedAsc(spc);
  const index = coreSorted.findIndex((tx) => tx.id === txId);
  if (index === -1) {
    return null;
  }
  const core = coreSorted[index];
  const coreTimestamp = core.transactionTimestamp ?? core.timestamp;
  const previousCoreTimestamp =
    index === 0
      ? -Infinity
      : coreSorted[index - 1].transactionTimestamp ??
        coreSorted[index - 1].timestamp;
  return findEpisode(spc.mrcStates, previousCoreTimestamp, coreTimestamp);
};

export const buildLiveStateFromCurrent = (
  spc: SingleProfitCalculationApiResponse,
): Transaction | null => {
  const core = coreSortedAsc(spc);
  return core[core.length - 1] ?? null;
};
